import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSeparatedWasteData, getSeparatedWasteDataExcel } from '../services/wasteService';
import { ArrowDownIcon } from '../assets/icons';
import Pagination from '../components/Pagination';
import { formatNumber } from '../helpers/stringHelpers';

const TIME_PERIODS = [
  { label: 'Tümü', value: '' },
  { label: 'Bugün', value: 0 },
  { label: '1 Hafta', value: 7 },
  { label: '1 Ay', value: 30 },
  { label: '1 Yıl', value: 365 }
];

const formatDescription = (text) => {
  if (!text || text.length <= 110) return text;

  const linkLength = 15;
  const truncateAt = 90 - linkLength;

  return <>{text.slice(0, truncateAt)}... <span style={{ color: '#18964E', fontWeight: 600, cursor: 'pointer' }}>Devamını Gör &gt;</span></>;
};

const getLatestReceivedRecordWithDescription = (records) => {
  return records
    .filter(r => r.dateReceived && r.description)
    .sort((a, b) => new Date(b.dateReceived) - new Date(a.dateReceived))[0];
};

const SeparatedWaste = () => {
  const navigate = useNavigate();
  const [wasteData, setWasteData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDays, setSelectedDays] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statistics, setStatistics] = useState({
    totalWasted: 0,
    totalApprovedWaste: 0,
    totalRejectWaste: 0
  });

  useEffect(() => {
    fetchWasteData();
  }, [currentPage, selectedDays]);

  const fetchWasteData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getSeparatedWasteData({
        offset: currentPage,
        days: selectedDays,
        search: searchTerm
      });

      if (response.status === 200 && response.data) {
        setWasteData(response.data);
        setTotalPages(Math.ceil(response.totalRecords / 15));
        setStatistics(response.statistics || {
          totalWasted: 0,
          totalApprovedWaste: 0,
          totalRejectWaste: 0
        });
      } else {
        setWasteData([]);
        setTotalPages(0);
        setStatistics({
          totalWasted: 0,
          totalApprovedWaste: 0,
          totalRejectWaste: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch separated waste data:', error);
      setError('Ayrıştırma Verileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handlePeriodChange = (e) => {
    setSelectedDays(e.target.value ? Number(e.target.value) : '');
    setCurrentPage(0);
  };

  const handleExcelDownload = async () => {
    try {
      await getSeparatedWasteDataExcel(selectedDays);
    } catch (error) {
      console.error('Failed to download excel:', error);
    }
  };

  const handleRowClick = (record) => {
    const encodedAddress = encodeURIComponent(record.address);
    navigate(`/ayristirma-detay?adres=${encodedAddress}`);
  };

  if (isLoading) {
    return (
      <div className="waste-container">
        <h1 className="page-title">Ayrıştırma Verileri</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="waste-container">
        <h1 className="page-title">Ayrıştırma Verileri</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="waste-container">
      <h1 className="page-title">Ayrıştırma Verileri</h1>

      {/* Search and Filter Section */}
      <div className="search-filter-section">

        <div className="filter-container">
          <button className="download-excel-button" onClick={handleExcelDownload}>
            <p>Excel Çıktısı Al</p>
            <ArrowDownIcon color='white' className="filter-icon" />
          </button>
          <select value={selectedDays} onChange={handlePeriodChange}>
            {TIME_PERIODS.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Updated Statistics Section */}
      <div className="statistics-section">
        <div className="stat-card-container separated-waste-statistics">
          <div className="stat-card">
            <h3>Toplam<br />Alınan Atık</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalWasted)}</p>
            <p className='stat-card-text'>adet</p>
          </div>
          <div className="stat-card red">
            <h3>Toplam<br />Reddedilen Atık Sayısı</h3>
            <p className='stat-card-number red'>{formatNumber(statistics.totalRejectWaste)}</p>
            <p className='stat-card-text'>adet</p>
          </div>
          <div className="stat-card">
            <h3>Toplam<br />Onaylanan Atık</h3>
            <p className='stat-card-number'>{formatNumber(statistics.totalApprovedWaste)}</p>
            <p className='stat-card-text'>adet</p>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-container">
        <table className="waste-table">
          <thead>
            <tr>
              {/* <th>Adres</th> */}
              <th>İlçe</th>
              <th>Mahalle</th>
              <th>Sokak</th>
              <th>Apartman Adı<br />Daire No</th>
              <th>Onaylanan<br />Atık Sayısı</th>
              <th>Reddedilen<br />Atık Sayısı</th>
              {/* <th>Çekilen<br />Resim Sayısı</th> */}
              {/* <th>Gidiş Sayısı</th> */}
              <th>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {wasteData.length > 0 ? (
              wasteData.map((record) => {
                const latestReceivedWithDesc = getLatestReceivedRecordWithDescription(record.records);
                const approvedCount = record.records.filter(r => r.status).length;
                const rejectedCount = record.records.filter(r => !r.status).length;
                const photoCount = record.records.reduce((total, r) => total + (r.photos?.length || 0), 0);

                return (
                  <tr
                    key={record.qr}
                    onClick={() => handleRowClick(record)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className='table-address'><span className='district'>{record.records[0].district}</span></td>
                    <td className='table-address'>{record.records[0].mahalle}</td>
                    <td className='table-address'>{record.records[0].sokak}</td>
                    <td className='table-address'>{record.records[0].apartman}<br /><span className='table-address-number'>{record.records[0].daire}</span></td>
                    <td className='table-count approved'>{approvedCount} Atık Onaylandı</td>
                    <td className='table-count rejected'>{rejectedCount} Atık Reddedildi</td>
                    {/* <td className='table-count'>{photoCount} adet</td>
                    <td className='table-badge-cell'>
                      <span className='table-badge'>
                        {new Set(record.records.map(r => r.dateGiven?.split('T')[0])).size}.Gidiş
                      </span>
                    </td> */}
                    <td>
                      <p className='description'>
                        {latestReceivedWithDesc ? formatDescription(latestReceivedWithDesc.description) : ''}
                      </p>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  Veri bulunamadı
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default SeparatedWaste;